import React, { useEffect, useState } from "react";
import { PortalHeader, PortalBody, StatTile, EmptyState, StatusPill } from "@/components/PortalUI";
import api from "@/lib/api";
import { CsvExportButton } from "@/pages/admin/AdminPhase2";

export function AdminMetrics() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/metrics").then((r) => setM(r.data)); }, []);
  return (
    <>
      <PortalHeader eyebrow="/ ADMIN · COMMAND CENTER" title="Metrics" testId="admin-metrics-header">
        <CsvExportButton collection="orders" label="Orders CSV" testId="metrics-export-orders" />
      </PortalHeader>
      <PortalBody>
        {!m ? (
          <EmptyState testId="metrics-loading">LOADING...</EmptyState>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatTile label="Audits · 7d" value={m.audits_this_week} testId="metrics-audits" />
              <StatTile label="Orders · 7d" value={m.orders_this_week} testId="metrics-orders" />
              <StatTile label="Contacts · 7d" value={m.contacts_this_week} testId="metrics-contacts" />
              <StatTile label="Total users" value={m.total_users} testId="metrics-users" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="dt6-card p-6" data-testid="metrics-by-module">
                <div className="font-mono text-[0.7rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)] mb-3">/ ORDERS BY MODULE</div>
                {m.orders_by_module.length === 0 ? (
                  <EmptyState testId="metrics-by-module-empty">NO ORDERS TO CHART.</EmptyState>
                ) : (
                  <div className="space-y-2">
                    {m.orders_by_module.map((row) => (
                      <div key={row.module} className="flex items-center gap-3">
                        <div className="font-mono text-xs w-10">{row.module}</div>
                        <div className="flex-1 h-2 bg-[color:var(--dt6-surface-2)] relative">
                          <div className="absolute inset-y-0 left-0 bg-[color:var(--dt6-primary)]" style={{ width: `${Math.min(100, row.count * 10)}%` }} />
                        </div>
                        <div className="font-mono text-xs w-10 text-right">{row.count}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="dt6-card p-6" data-testid="metrics-by-status">
                <div className="font-mono text-[0.7rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)] mb-3">/ ORDERS BY STATUS</div>
                {m.orders_by_status.length === 0 ? (
                  <EmptyState testId="metrics-by-status-empty">NO ORDERS TO CHART.</EmptyState>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {m.orders_by_status.map((row) => (
                      <div key={row.status} className="dt6-card px-3 py-2 flex items-center gap-3" data-testid={`metrics-status-${row.status}`}>
                        <StatusPill status={row.status} />
                        <div className="font-mono">{row.count}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </PortalBody>
    </>
  );
}

function StatusSelect({ current, options, onChange, testId }) {
  return (
    <select className="dt6-select text-xs py-1" value={current || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      <option value="">— set status —</option>
      {options.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
}

const STATUS_OPTIONS = {
  audit_requests: ["received","in_review","in_progress","delivered","cancelled"],
  orders: ["received","in_review","in_progress","delivered","cancelled"],
  contacts: ["new","in_review","closed"],
  partner_applications: ["new","approved","declined"],
  dsar_requests: ["received","in_progress","fulfilled"],
};

export function AdminPipeline() {
  const [data, setData] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get("/admin/pipeline").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (collection, id, status) => {
    await api.put(`/admin/${collection}/${id}/status`, { status });
    await load();
    if (selectedItem && selectedItem.id === id) setSelectedItem({ ...selectedItem, status });
  };
  const addNote = async () => {
    if (!selectedItem || !note.trim() || !selectedCollection) return;
    await api.post(`/admin/${selectedCollection}/${selectedItem.id}/note`, { body: note });
    setNote("");
    await load();
  };

  const columns = [
    { key: "audit_requests", label: "Audit requests", get: (d) => d.audit_requests, title: (i) => i.org_name || i.email, meta: (i) => i.state },
    { key: "orders", label: "Orders", get: (d) => d.orders, title: (i) => `${i.module_code} · ${i.order_type || ""}`, meta: (i) => i.created_by_email },
    { key: "contacts", label: "Contacts", get: (d) => d.contacts, title: (i) => i.name, meta: (i) => i.email },
    { key: "partner_applications", label: "Partner apps", get: (d) => d.partner_applications, title: (i) => i.org_name, meta: (i) => i.email },
    { key: "dsar_requests", label: "DSAR", get: (d) => d.dsar_requests, title: (i) => i.full_name, meta: (i) => i.request_type },
  ];

  return (
    <>
      <PortalHeader eyebrow="/ ADMIN" title="Pipeline board" testId="admin-pipeline-header" />
      <PortalBody>
        {!data ? (
          <EmptyState testId="pipeline-loading">LOADING...</EmptyState>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-kanban">
            {columns.map((col) => {
              const items = col.get(data);
              return (
                <div key={col.key} className="dt6-kanban-col p-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-mono text-[0.72rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)]">/ {col.label}</div>
                    <div className="font-mono text-xs">{items.length}</div>
                  </div>
                  <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                    {items.length === 0 && <EmptyState testId={`pipeline-${col.key}-empty`}>NONE</EmptyState>}
                    {items.map((it) => (
                      <div
                        key={it.id}
                        onClick={() => { setSelectedItem(it); setSelectedCollection(col.key); }}
                        className="dt6-card p-3 cursor-pointer hover:border-[color:var(--dt6-primary)]"
                        data-testid={`pipeline-card-${it.id}`}
                      >
                        <div className="text-sm font-medium truncate">{col.title(it)}</div>
                        <div className="text-xs text-[color:var(--dt6-muted)] truncate">{col.meta(it)}</div>
                        <div className="mt-2 flex items-center justify-between">
                          <StatusPill status={it.status} />
                          <span className="font-mono text-[0.65rem] text-[color:var(--dt6-muted)]">
                            {new Date(it.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {selectedItem && selectedCollection && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelectedItem(null)} data-testid="pipeline-drilldown">
            <div className="dt6-card w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()} style={{ background: "var(--dt6-surface)" }}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="dt6-chip mb-2">{selectedCollection.replace("_", " ")}</div>
                  <div className="font-mono text-xs text-[color:var(--dt6-muted)]">ID {selectedItem.id.slice(0,8).toUpperCase()}</div>
                </div>
                <button className="dt6-btn text-xs" onClick={() => setSelectedItem(null)} data-testid="pipeline-drill-close">Close</button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {Object.entries(selectedItem).filter(([k]) => !["_id","internal_notes","deliverables","adapter_result","parameters"].includes(k)).map(([k,v]) => (
                  <div key={k}>
                    <div className="dt6-label">{k}</div>
                    <div className="break-all">{typeof v === "object" ? JSON.stringify(v) : String(v ?? "—")}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center gap-3">
                <StatusSelect
                  current={selectedItem.status}
                  options={STATUS_OPTIONS[selectedCollection] || []}
                  onChange={(v) => updateStatus(selectedCollection, selectedItem.id, v)}
                  testId="pipeline-drill-status"
                />
                <input className="dt6-input" placeholder="add internal note..." value={note} onChange={(e)=>setNote(e.target.value)} data-testid="pipeline-drill-note" />
                <button className="dt6-btn" onClick={addNote} data-testid="pipeline-drill-note-submit">Log note</button>
              </div>
              {selectedItem.internal_notes?.length > 0 && (
                <div className="mt-4">
                  <div className="dt6-label">/ notes</div>
                  <div className="space-y-2 mt-2 max-h-40 overflow-y-auto">
                    {selectedItem.internal_notes.map((n) => (
                      <div key={n.id} className="text-xs border-l-2 border-[color:var(--dt6-primary)] pl-3">
                        <div className="text-[color:var(--dt6-muted)] font-mono">{n.author} · {new Date(n.at).toLocaleString()}</div>
                        <div>{n.body}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </PortalBody>
    </>
  );
}

export function AdminFulfillment() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/pipeline").then((r) => setData(r.data)); }, []);
  const items = data
    ? [...(data.orders || []), ...(data.audit_requests || [])].filter((x) => !["delivered","cancelled","fulfilled","closed","declined"].includes(x.status))
        .sort((a,b) => new Date(a.created_at) - new Date(b.created_at))
    : [];
  return (
    <>
      <PortalHeader eyebrow="/ ADMIN" title="Fulfillment queue" testId="admin-fulfillment-header" />
      <PortalBody>
        {!data ? (
          <EmptyState testId="fulfillment-loading">LOADING...</EmptyState>
        ) : items.length === 0 ? (
          <EmptyState testId="fulfillment-empty">FULFILLMENT QUEUE IS CLEAR.</EmptyState>
        ) : (
          <div className="dt6-card overflow-hidden">
            <table className="dt6-table">
              <thead>
                <tr><th>Age</th><th>Type</th><th>Ref</th><th>Contact</th><th>Status</th></tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const age = Math.round((Date.now() - new Date(it.created_at)) / (1000 * 60 * 60));
                  const type = it.module_code ? "order" : "audit";
                  return (
                    <tr key={it.id} data-testid={`fulfillment-row-${it.id}`}>
                      <td className="font-mono">{age < 24 ? `${age}h` : `${Math.round(age/24)}d`}</td>
                      <td><span className="font-mono">{type === "order" ? it.module_code : "AUDIT"}</span></td>
                      <td className="font-mono text-xs">{it.id.slice(0,8).toUpperCase()}</td>
                      <td className="text-sm">{it.email || it.created_by_email || "—"}</td>
                      <td><StatusPill status={it.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </PortalBody>
    </>
  );
}

export function AdminAuditLog() {
  const [logs, setLogs] = useState([]);
  const [type, setType] = useState("");
  const [target, setTarget] = useState("");
  const load = () => {
    const params = {};
    if (type) params.event_type = type;
    if (target) params.target_type = target;
    api.get("/admin/audit-log", { params }).then((r) => setLogs(r.data));
  };
  useEffect(load, [type, target]);
  return (
    <>
      <PortalHeader eyebrow="/ ADMIN" title="Audit log" testId="admin-log-header">
        <input className="dt6-input" placeholder="event type" value={type} onChange={(e)=>setType(e.target.value)} data-testid="log-filter-type" style={{width:180}} />
        <input className="dt6-input" placeholder="target type" value={target} onChange={(e)=>setTarget(e.target.value)} data-testid="log-filter-target" style={{width:180}} />
      </PortalHeader>
      <PortalBody>
        {logs.length === 0 ? (
          <EmptyState testId="log-empty">NO EVENTS MATCH FILTER.</EmptyState>
        ) : (
          <div className="dt6-card overflow-hidden">
            <table className="dt6-table">
              <thead>
                <tr><th>Time</th><th>Actor</th><th>Event</th><th>Target</th><th>Meta</th></tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} data-testid={`log-row-${l.id}`}>
                    <td className="font-mono text-xs">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="text-xs">{l.actor_email || <span className="text-[color:var(--dt6-muted)]">— anonymous —</span>}</td>
                    <td className="font-mono text-xs">{l.event_type}</td>
                    <td className="font-mono text-xs">{l.target_type} / {String(l.target_id).slice(0,8)}</td>
                    <td className="text-xs text-[color:var(--dt6-muted)]">{Object.keys(l.meta||{}).length ? JSON.stringify(l.meta) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PortalBody>
    </>
  );
}

export function AdminOutbox() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/outbox").then((r) => setRows(r.data)); }, []);
  return (
    <>
      <PortalHeader eyebrow="/ ADMIN" title="Outbox" testId="admin-outbox-header" />
      <PortalBody>
        <div className="mb-4 text-xs text-[color:var(--dt6-muted)] font-mono tracking-[0.14em] uppercase">
          / EMAIL DELIVERY IS <span className="text-[color:var(--dt6-secondary)]">MOCKED</span> IN THIS ENVIRONMENT — ALL SENDS ARE LOGGED HERE.
        </div>
        {rows.length === 0 ? (
          <EmptyState testId="outbox-empty">OUTBOX IS EMPTY.</EmptyState>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.id} className="dt6-card p-4" data-testid={`outbox-row-${r.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium">{r.subject}</div>
                  <span className="dt6-chip">{r.category}</span>
                </div>
                <div className="text-xs text-[color:var(--dt6-muted)]">to: {r.to} · {new Date(r.created_at).toLocaleString()}</div>
                <pre className="mt-3 whitespace-pre-wrap text-xs text-[color:var(--dt6-muted)]">{r.body}</pre>
              </div>
            ))}
          </div>
        )}
      </PortalBody>
    </>
  );
}

export function AdminAdapterQueue() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/adapter-queue").then((r) => setRows(r.data)); }, []);
  return (
    <>
      <PortalHeader eyebrow="/ ADMIN" title="Adapter queue" testId="admin-adapters-header" />
      <PortalBody>
        <div className="mb-4 text-xs text-[color:var(--dt6-muted)] font-mono tracking-[0.14em] uppercase">
          / VENDOR ADAPTERS ARE <span className="text-[color:var(--dt6-secondary)]">NOT_CONNECTED</span> IN THIS ENVIRONMENT.
          JOBS ROUTE HERE FOR MANUAL FULFILLMENT.
        </div>
        {rows.length === 0 ? (
          <EmptyState testId="adapters-empty">NO ADAPTER JOBS.</EmptyState>
        ) : (
          <div className="dt6-card overflow-hidden">
            <table className="dt6-table">
              <thead><tr><th>Adapter</th><th>Vendor class</th><th>Order</th><th>Status</th><th>Created</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`adapters-row-${r.id}`}>
                    <td className="font-mono">{r.adapter}</td>
                    <td className="font-mono text-xs">{r.vendor_class}</td>
                    <td className="font-mono text-xs">{r.order_id?.slice(0,8).toUpperCase()}</td>
                    <td><span className="dt6-chip dt6-chip-amber">{r.reason}</span></td>
                    <td className="font-mono text-xs text-[color:var(--dt6-muted)]">{new Date(r.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PortalBody>
    </>
  );
}

export function AdminCRM() {
  const [people, setPeople] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/crm/people").then((r) => setPeople(r.data)); }, []);
  const filtered = people.filter((p) => !q || p.email.includes(q.toLowerCase()) || (p.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <PortalHeader eyebrow="/ ADMIN" title="CRM lite" testId="admin-crm-header">
        <input className="dt6-input" placeholder="search email or name" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="crm-search" style={{width:280}} />
      </PortalHeader>
      <PortalBody>
        {filtered.length === 0 ? (
          <EmptyState testId="crm-empty">NO PEOPLE YET.</EmptyState>
        ) : (
          <div className="dt6-card overflow-hidden">
            <table className="dt6-table">
              <thead><tr><th>Email</th><th>Name</th><th>Sources</th><th>Activity</th></tr></thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.email} data-testid={`crm-row-${p.email}`}>
                    <td className="text-sm">{p.email}</td>
                    <td className="text-sm">{p.name || "—"}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {p.sources.map((s) => <span key={s} className="dt6-chip text-[0.65rem]">{s}</span>)}
                      </div>
                    </td>
                    <td className="text-xs text-[color:var(--dt6-muted)]">{p.activity.length} events</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PortalBody>
    </>
  );
}
